import type { FemaEnvironmentChoice } from 'features/Weather/features/WeatherAlerts/features/AlertCreator/components/IpawsAlertFields/config/femaEnvironment.config';
import { useEffect, useMemo, useState } from 'react';
import { Form } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { useGetGlobalActionsQuery } from 'services/globalActions/globalActions.api';
import { useGetFemaAlertsQuery } from 'services/ipaws/ipaws.api';
import AlertList from '../AlertList/index.web';
import { setAlertList } from '../AlertList/state/slices/alertListSlice';
import { groupFemaMessagesIntoThreads } from './utils/femaMessage.utils';
import { femaThreadToAlert, parseFemaMessageEnvironment } from './utils/femaToAlert.adapter';

type PermsLike = {
  testPermission: (levels: number[], permissions: string[]) => boolean;
};

/** The class filter options. The empty value sends no `environment` parameter. */
const FEMA_ENVIRONMENT_FILTER_OPTIONS: readonly {
  value: FemaEnvironmentChoice | '';
  label: string;
}[] = [
  { value: '', label: 'All classes' },
  { value: 'tdl', label: 'TDL' },
  { value: 'demo', label: 'Test / Demo' },
  { value: 'production', label: 'LIVE PRODUCTION' },
];

type FemaAlertListProps = {
  title?: string;
  perms: PermsLike;
  canDeleteAlerts?: boolean;
};

/**
 * Loads FEMA messages into the shared alert-list state so FEMA uses the same list and card flow
 * as other alert providers.
 */
const FemaAlertList = ({ title = 'FEMA Alerts', perms, canDeleteAlerts }: FemaAlertListProps) => {
  const dispatch = useDispatch();
  const [environmentFilter, setEnvironmentFilter] = useState<FemaEnvironmentChoice | null>(null);
  // `GET /fusion/v2/fema?environment=<class>` filters the list. No parameter: every class that
  // the workplace holds.
  const { data: records = [], isLoading } = useGetFemaAlertsQuery(
    environmentFilter ? { environment: environmentFilter } : {},
  );
  const hasGlobalActionAlerts = records.some((record) => record.global_action_id != null);
  const canViewGlobalActions = perms.testPermission([8, 9, 10, 11, 12, 13], ['ViewGlobalActions']);
  const { data: globalActions = [] } = useGetGlobalActionsQuery(undefined, {
    skip: !hasGlobalActionAlerts || !canViewGlobalActions,
  });

  const alerts = useMemo(() => {
    const globalActionTitles = new Map(
      globalActions.map((action) => [String(action.id ?? action.gaId), action.title]),
    );

    return groupFemaMessagesIntoThreads(records)
      .map((thread) => femaThreadToAlert(thread))
      .filter((alert): alert is NonNullable<typeof alert> => Boolean(alert))
      .map((alert) => ({
        ...alert,
        globalActionTitle:
          alert.globalActionTitle ??
          (alert.globalActionId != null
            ? (globalActionTitles.get(String(alert.globalActionId)) ?? null)
            : null),
        alertSource: 'fema' as const,
      }));
  }, [globalActions, records]);

  useEffect(() => {
    dispatch(setAlertList(alerts));
  }, [alerts, dispatch]);

  return (
    <div className="d-flex flex-column h-100">
      <Form.Group
        controlId="fema-environment-filter"
        className="d-flex align-items-center gap-2 px-3 pt-3"
      >
        <Form.Label className="mb-0 text-nowrap">FEMA environment</Form.Label>
        <Form.Select
          size="sm"
          className="w-auto"
          value={environmentFilter ?? ''}
          onChange={(event) =>
            setEnvironmentFilter(parseFemaMessageEnvironment(event.target.value))
          }
        >
          {FEMA_ENVIRONMENT_FILTER_OPTIONS.map((option) => (
            <option key={option.value || 'all'} value={option.value}>
              {option.label}
            </option>
          ))}
        </Form.Select>
      </Form.Group>
      {isLoading ? <p className="w-100 py-3 text-center">Loading FEMA messages...</p> : null}
      {!isLoading ? (
        <AlertList title={title} perms={perms} canDeleteAlerts={canDeleteAlerts} />
      ) : null}
    </div>
  );
};

export default FemaAlertList;
